import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../contexts/AuthContext';
import { useFleetProfile } from '../hooks/useFleet';
import fleetService from '../services/fleetService';
import { User, MapPin, Building2, Loader2, Mail, Phone, MapPinned, Settings, Handshake } from 'lucide-react';
import { FloatingNavBar } from './Navbar';

const defaultFormState = () => ({
  name: '',
  contact_info: '',
  address: '',
  city: '',
  state: '',
  pincode: '',
  emergency_contact: '',
  service_area: '',
  auto_accept_partnerships: false,
  partnership_preferences: {
    preferred_hospitals: '',
    max_distance_km: ''
  }
});

const FleetProfileEdit = ({ onBackToProfile }) => {
  const { user: authUser } = useAuth();
  const { data: profileData, loading: profileLoading, error: profileError } = useFleetProfile();

  const [formData, setFormData] = useState(defaultFormState());
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Maps GET /v1/fleet/auth/profile response (and nested partnership_preferences shape) to form state
  const mapProfileToForm = (data) => {
    if (!data) return defaultFormState();
    const prefs = data.partnership_preferences;
    // API can return preferred_hospitals/max_distance at top level or inside partnership_preferences
    const preferredHospitals = prefs?.preferred_hospitals ?? data.preferred_hospitals;
    const maxDistance = prefs?.max_distance_km ?? data.max_distance;
    return {
      name: data.name ?? '',
      contact_info: data.contact_info ?? '',
      address: data.address ?? '',
      city: data.city ?? '',
      state: data.state ?? '',
      pincode: data.pincode != null ? String(data.pincode) : '',
      emergency_contact: data.emergency_contact ?? '',
      service_area: data.service_area ?? '',
      auto_accept_partnerships: Boolean(data.auto_accept_partnerships),
      partnership_preferences: {
        preferred_hospitals: Array.isArray(preferredHospitals)
          ? preferredHospitals.join(', ')
          : (typeof preferredHospitals === 'string' ? preferredHospitals : ''),
        max_distance_km: maxDistance != null ? String(maxDistance) : ''
      }
    };
  };

  useEffect(() => {
    // Support both flat (data) and double-nested (data.data) API response shapes
    const rawProfile = profileData?.data?.data ?? profileData?.data;
    if (profileData?.success && rawProfile) {
      setFormData(mapProfileToForm(rawProfile));
    } else if (authUser) {
      setFormData(prev => ({
        ...prev,
        name: authUser.name ?? prev.name,
        contact_info: authUser.contact_info ?? prev.contact_info,
        address: authUser.address ?? prev.address,
        city: authUser.city ?? prev.city,
        state: authUser.state ?? prev.state,
        pincode: authUser.pincode != null ? String(authUser.pincode) : prev.pincode,
        service_area: authUser.service_area ?? prev.service_area
      }));
    }
  }, [profileData, authUser]);

  const handleInputChange = (field, value) => {
    if (field === 'auto_accept_partnerships') {
      setFormData(prev => ({ ...prev, [field]: Boolean(value) }));
    } else if (field === 'preferred_hospitals' || field === 'max_distance_km') {
      setFormData(prev => ({
        ...prev,
        partnership_preferences: {
          ...prev.partnership_preferences,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    if (saveError) setSaveError(null);
    if (saveSuccess) setSaveSuccess(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const preferredHospitalsStr = formData.partnership_preferences?.preferred_hospitals ?? '';
      const preferredHospitals = preferredHospitalsStr
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      const maxDistanceKm = Number(formData.partnership_preferences?.max_distance_km) || 0;

      const body = {
        name: formData.name,
        contact_info: formData.contact_info,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
        emergency_contact: formData.emergency_contact,
        service_area: formData.service_area,
        auto_accept_partnerships: formData.auto_accept_partnerships,
        partnership_preferences: {
          preferred_hospitals: preferredHospitals,
          max_distance_km: maxDistanceKm
        }
      };

      const result = await fleetService.updateMyProfile(body);

      if (result.success) {
        setSaveSuccess(true);
        if (result.data) {
          setFormData(mapProfileToForm(result.data));
        }
      } else {
        throw new Error(result.message || 'Failed to update profile');
      }
    } catch (error) {
      if (error.errors && Array.isArray(error.errors)) {
        setSaveError(error.errors.map(err => `${err.field}: ${err.message}`).join(', '));
      } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        setSaveError(error.response.data.errors.map(err => `${err.field}: ${err.message}`).join(', '));
      } else if (error.response?.data?.message) {
        setSaveError(error.response.data.message);
      } else if (error.message) {
        setSaveError(error.message);
      } else {
        setSaveError('Failed to update profile. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const inputClass = 'w-full pl-10 pr-4 py-2.5 bg-gray-50/80 border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:bg-white focus:border-slate-400 focus:ring-2 focus:ring-slate-400/20 outline-none transition-all';
  const labelClass = 'block text-sm font-medium text-slate-600';

  if (profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100/60">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
          <p className="text-slate-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (profileError && !authUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100/60">
        <div className="text-center">
          <p className="text-red-600 mb-2">Failed to load profile</p>
          <p className="text-sm text-slate-600">{profileError.message || 'Please try again later'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-8xl mx-auto px-4 py-8 flex flex-col items-center">
        <div className="w-full rounded-2xl backdrop-blur-xl border-2 border-slate-300 shadow-xl shadow-slate-200/50 overflow-hidden">
          <div className="px-6 sm:px-8 pt-6 sm:pt-8 pb-4 border-b border-slate-100">
            <h1 className="text-xl sm:text-2xl font-semibold text-slate-800 tracking-tight">Update Profile</h1>
            <p className="mt-1 text-sm text-slate-500">Edit your fleet operator details</p>
          </div>

          <div className="px-6 sm:px-8 py-6 sm:py-8">
            {saveSuccess && (
              <div className="mb-6 p-3.5 rounded-xl bg-emerald-50 border border-emerald-200/80">
                <p className="text-sm font-medium text-emerald-800">Profile updated successfully!</p>
              </div>
            )}

            {saveError && (
              <div className="mb-6 p-3.5 rounded-xl bg-red-50 border border-red-200/80">
                <p className="text-sm font-medium text-red-700">{saveError}</p>
              </div>
            )}

            <form className="space-y-8" onSubmit={handleSave}>
              <div className="space-y-4">
                <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-2 pb-2 border-b border-slate-100">
                  <Building2 className="w-4 h-4 text-slate-500" /> Fleet Information
                </h2>

                <div className="space-y-2">
                  <label htmlFor="name" className={labelClass}>Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className={inputClass}
                      placeholder="e.g. Reliable Fleet Services"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="contact_info" className={labelClass}>Contact info *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input
                      id="contact_info"
                      type="text"
                      value={formData.contact_info}
                      onChange={(e) => handleInputChange('contact_info', e.target.value)}
                      className={inputClass}
                      placeholder="e.g. support@reliablefleet.com or phone"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="address" className={labelClass}>Address</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 text-slate-400 w-4 h-4" />
                    <textarea
                      id="address"
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      className={`${inputClass} min-h-[80px] py-3`}
                      placeholder="e.g. 123 Transport Lane, Logistics Hub"
                      rows={2}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label htmlFor="city" className={labelClass}>City</label>
                    <input
                      id="city"
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className={inputClass}
                      placeholder="City"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="state" className={labelClass}>State</label>
                    <input
                      id="state"
                      type="text"
                      value={formData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      className={inputClass}
                      placeholder="State"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="pincode" className={labelClass}>Pincode</label>
                    <input
                      id="pincode"
                      type="text"
                      value={formData.pincode}
                      onChange={(e) => handleInputChange('pincode', (e.target.value || '').replace(/\D/g, '').slice(0, 6))}
                      maxLength={6}
                      className={inputClass}
                      placeholder="e.g. 400001"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="emergency_contact" className={labelClass}>Emergency contact</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input
                      id="emergency_contact"
                      type="tel"
                      value={formData.emergency_contact}
                      onChange={(e) => handleInputChange('emergency_contact', e.target.value)}
                      className={inputClass}
                      placeholder="e.g. 9876543210"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="service_area" className={labelClass}>Service area</label>
                  <div className="relative">
                    <MapPinned className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input
                      id="service_area"
                      type="text"
                      value={formData.service_area}
                      onChange={(e) => handleInputChange('service_area', e.target.value)}
                      className={inputClass}
                      placeholder="e.g. Greater Mumbai Region"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    id="auto_accept_partnerships"
                    type="checkbox"
                    checked={formData.auto_accept_partnerships}
                    onChange={(e) => handleInputChange('auto_accept_partnerships', e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-slate-600 focus:ring-slate-400"
                  />
                  <label htmlFor="auto_accept_partnerships" className={labelClass}>Auto accept partnerships</label>
                </div>
              </div>

              <div className="space-y-4">
                <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-2 pb-2 border-b border-slate-100">
                  <Handshake className="w-4 h-4 text-slate-500" /> Partnership preferences
                </h2>

                <div className="space-y-2">
                  <label htmlFor="preferred_hospitals" className={labelClass}>Preferred hospitals</label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input
                      id="preferred_hospitals"
                      type="text"
                      value={formData.partnership_preferences?.preferred_hospitals ?? ''}
                      onChange={(e) => handleInputChange('preferred_hospitals', e.target.value)}
                      className={inputClass}
                      placeholder="Comma-separated, e.g. apollo, fortis"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="max_distance_km" className={labelClass}>Max distance (km)</label>
                  <div className="relative">
                    <Settings className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input
                      id="max_distance_km"
                      type="number"
                      min={0}
                      step={1}
                      value={formData.partnership_preferences?.max_distance_km ?? ''}
                      onChange={(e) => handleInputChange('max_distance_km', e.target.value)}
                      className={inputClass}
                      placeholder="e.g. 100"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={onBackToProfile}
                  disabled={isSaving}
                  className="px-5 py-2.5 text-sm font-medium text-slate-600 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-5 py-2.5 text-sm font-medium text-white rounded-xl bg-slate-700 hover:bg-slate-800 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <FloatingNavBar
        tabs={['Profile', 'Notifications', 'Edit Profile']}
        activeTab="Edit Profile"
        onTabChange={(tab) => {
          if (tab === 'Profile' && onBackToProfile) onBackToProfile();
        }}
        showLogout={false}
        className="mt-8"
      />
    </div>
  );
};

export default FleetProfileEdit;

FleetProfileEdit.propTypes = {
  onBackToProfile: PropTypes.func.isRequired
};
